/** @typedef {'all' | 'upi' | 'card' | 'netbanking' | 'wallet'} PaymentMethodId */

/** @typedef {'recommended' | 'cards' | 'more'} PaymentMethodSectionId */

/**
 * @typedef {{
 *   id: PaymentMethodId,
 *   label: string,
 *   subtitle: string,
 *   section: PaymentMethodSectionId,
 *   sectionLabel?: string,
 * }} PaymentMethodOption
 */

/** @type {Record<PaymentMethodSectionId, string>} */
export const PAYMENT_SECTION_LABELS = {
  recommended: 'Recommended',
  cards: 'Credit & debit cards',
  more: 'More payment options',
};

/** @type {PaymentMethodOption[]} */
export const PAYMENT_METHOD_OPTIONS = [
  {
    id: 'upi',
    label: 'UPI',
    subtitle: 'Google Pay, PhonePe, Paytm & more',
    section: 'recommended',
    sectionLabel: PAYMENT_SECTION_LABELS.recommended,
  },
  {
    id: 'card',
    label: 'Credit & debit cards',
    subtitle: 'Visa, Mastercard, RuPay & more',
    section: 'cards',
    sectionLabel: PAYMENT_SECTION_LABELS.cards,
  },
  {
    id: 'netbanking',
    label: 'Net banking',
    subtitle: 'Select from a list of banks',
    section: 'more',
    sectionLabel: PAYMENT_SECTION_LABELS.more,
  },
  {
    id: 'wallet',
    label: 'Wallets',
    subtitle: 'Paytm, Amazon Pay & more',
    section: 'more',
  },
  {
    id: 'all',
    label: 'All payment methods',
    subtitle: 'UPI, cards, net banking & wallets',
    section: 'more',
  },
];

/** @type {PaymentMethodSectionId[]} */
export const PAYMENT_SECTION_ORDER = ['recommended', 'cards', 'more'];

/**
 * @param {PaymentMethodSectionId} sectionId
 * @returns {PaymentMethodOption[]}
 */
export function getMethodsForSection(sectionId) {
  return PAYMENT_METHOD_OPTIONS.filter((m) => m.section === sectionId);
}

/** @returns {PaymentMethodId} */
export function getDefaultPaymentMethodId() {
  return 'all';
}

/** Major banks shown as illustrative chips (Razorpay shows full list). */
export const PAYMENT_NETBANKING_CHIPS = [
  'HDFC',
  'ICICI',
  'SBI',
  'Axis',
  'Kotak',
  'PNB',
];

/** UPI apps shown as illustrative chips. */
export const PAYMENT_UPI_CHIPS = ['Google Pay', 'PhonePe', 'Paytm', 'BHIM'];

/** Wallet names shown as illustrative chips. */
export const PAYMENT_WALLET_CHIPS = ['Paytm', 'Amazon Pay', 'Mobikwik', 'Freecharge'];

/**
 * Razorpay Checkout `method` object (Phase 3).
 * @param {PaymentMethodId} selectedId
 * @returns {Record<string, boolean> | undefined}
 */
export function razorpayMethodConfig(selectedId) {
  if (selectedId === 'all') return undefined;

  const allowed = ['upi', 'card', 'netbanking', 'wallet'];
  if (allowed.includes(selectedId)) {
    return { [selectedId]: true };
  }
  return undefined;
}
